import React, { useState, useEffect, useCallback } from 'react';
import { Typography, DatePicker, TimePicker, Select, Switch, Row, Col, Spin, Divider, Tag, List, Modal, Alert, Button, Form, Input, Card, message } from 'antd';
import { AlertTwoTone, WarningTwoTone, CloseCircleTwoTone, IdcardTwoTone, StarTwoTone, ToolTwoTone, EyeTwoTone, EyeInvisibleTwoTone, CreditCardTwoTone, SyncOutlined, RocketTwoTone, HistoryOutlined, SafetyCertificateTwoTone, CheckCircleTwoTone, ClockCircleTwoTone, ExperimentTwoTone, ScanOutlined, DownloadOutlined, LockOutlined } from '@ant-design/icons';
import { useOutletContext, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PLANS, DEFAULT_PLAN } from '../../data/plans';

const { Title, Paragraph, Text } = Typography;

interface ProfilePageProps {
  token: string;
  API_URL: string;
}

interface Subscription {
  subscription_status: string;
  subscription_expires_at: string | null;
  is_premium: boolean;
  days_remaining: number | null;
}

interface PaymentRecord {
  id: string;
  invoice_id: string;
  amount: number;
  status: string;
  payment_method: string;
  membership_extended_days: number;
  new_expires_at: string;
  paid_at: string;
  created_at: string;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ token, API_URL }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [unknownTob, setUnknownTob] = useState(false);
  const [checksRemaining, setChecksRemaining] = useState<number | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPreview, setAdminPreview] = useState(false);

  // Subscription states
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>(DEFAULT_PLAN);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [checkoutInvoice, setCheckoutInvoice] = useState<string | null>(null);
  const [checkoutAmount, setCheckoutAmount] = useState<number>(PLANS.monthly.price);
  const [checkoutDays, setCheckoutDays] = useState<number>(30);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [qrisDataUrl, setQrisDataUrl] = useState<string | null>(null);
  const [checkoutUniqueCode, setCheckoutUniqueCode] = useState<number | null>(null);
  const [checkoutBaseAmount, setCheckoutBaseAmount] = useState<number | null>(null);

  const [passwordForm] = Form.useForm();
  const [form] = Form.useForm();
  const outletCtx = useOutletContext<{ refreshProfile?: () => void }>();
  const refreshProfile = outletCtx?.refreshProfile || (() => {});

  const [changingPassword, setChangingPassword] = useState(false);
  const [pwErrorMsg, setPwErrorMsg] = useState('');
  const [pwSuccessMsg, setPwSuccessMsg] = useState('');

  const fetchSubscription = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/personal/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
      }
    } catch (e) {
      console.error('Failed to fetch subscription', e);
    }
  }, [token, API_URL]);

  const fetchPaymentHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/personal/subscription/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentHistory(data.data || []);
      }
    } catch (e) {
      console.error('Failed to fetch payment history', e);
    }
    setLoadingHistory(false);
  }, [token, API_URL]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/personal/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setChecksRemaining(result.checks_remaining);
          if (result.user) {
            setUserEmail(result.user.email);
            setIsAdmin(result.user.email ? ['admin@bazi.com', 'developer@bazi.com', 'support@bazi.com'].includes(result.user.email.toLowerCase().trim()) : false);
            form.setFieldsValue({
              name: result.user.name || '',
              gender: result.user.gender || 'male',
              timezone: result.user.bazi_birth_timezone || 'Asia/Jakarta',
              dob: result.user.bazi_birth_date ? dayjs(result.user.bazi_birth_date) : undefined,
              tob: result.user.bazi_birth_time && result.user.bazi_birth_time !== '00:00:00'
                ? dayjs(result.user.bazi_birth_time, 'HH:mm:ss')
                : undefined
            });
            if (result.user.bazi_birth_time === '00:00:00') {
              setUnknownTob(true);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
    fetchSubscription();
    fetchPaymentHistory();
  }, [token, API_URL, form, fetchSubscription, fetchPaymentHistory]);

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const dobStr = values.dob ? values.dob.format('YYYY-MM-DD') : '';
      const tobStr = unknownTob ? '00:00' : (values.tob ? values.tob.format('HH:mm') : '12:00');

      const res = await fetch(`${API_URL}/api/v1/personal/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: values.name,
          gender: values.gender,
          date_of_birth: dobStr,
          time_of_birth: tobStr,
          timezone: values.timezone
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Gagal memperbarui profil lahir');
      }

      setSuccessMsg('Profil lahir kamu berhasil diperbarui!');
      refreshProfile();
    } catch (err: any) {
      setErrorMsg(err.message || 'Koneksi ke server gagal.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (values: any) => {
    setChangingPassword(true);
    setPwErrorMsg('');
    setPwSuccessMsg('');
    try {
      if (values.new_password !== values.confirm_password) {
        throw new Error('Konfirmasi password baru tidak cocok');
      }

      const res = await fetch(`${API_URL}/api/v1/personal/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: values.current_password,
          new_password: values.new_password
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Gagal mengubah password');

      setPwSuccessMsg('Password berhasil diubah!');
      passwordForm.resetFields();
    } catch (err: any) {
      setPwErrorMsg(err.message || 'Koneksi ke server gagal.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const planKey = selectedPlan;
      const plan = PLANS[planKey] || PLANS[DEFAULT_PLAN];
      const res = await fetch(`${API_URL}/api/v1/personal/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan: planKey })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Gagal memulai pembayaran');
      
      setCheckoutInvoice(data.invoice_id);
      setCheckoutAmount(data.amount || plan.price);
      setCheckoutDays(data.days || plan.days);
      setCheckoutUniqueCode(data.unique_code ?? null);
      setCheckoutBaseAmount(data.base_amount ?? null);
      
      if (data.qris && data.qris.qr_data_url) {
        // Dynamic QRIS from 3QRIS engine
        setQrisDataUrl(data.qris.qr_data_url);
        setShowPaymentModal(true);
      } else if (data.is_mock || !data.snap_token || data.snap_token === 'mock-snap-token') {
        // Show mock payment info with static QRIS fallback
        setQrisDataUrl(null);
        setShowPaymentModal(true);
      } else {
        // Load Midtrans Snap
        setQrisDataUrl(null);
        setShowPaymentModal(true);
        // Inject Midtrans Snap
        if ((window as any).snap) {
          (window as any).snap.pay(data.snap_token, {
            onSuccess: () => {
              setShowPaymentModal(false);
              fetchSubscription();
              fetchPaymentHistory();
            },
            onPending: () => {
              setShowPaymentModal(false);
              fetchPaymentHistory();
            },
            onError: () => {
              setShowPaymentModal(false);
            }
          });
        }
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal memulai pembayaran');
    }
    setCheckoutLoading(false);
  };

  // Subscription status display
  const renderSubscriptionBanner = () => {
    if (!subscription || isAdmin) return null;

    const { subscription_status, days_remaining, subscription_expires_at } = subscription;
    
    if (subscription_status === 'premium' && days_remaining !== null && days_remaining <= 3) {
      const urgency = days_remaining <= 1 ? 'error' : 'warning';
      const icon = days_remaining <= 1 ? <AlertTwoTone twoToneColor="#f5222d" style={{ marginRight: 8 }} /> : <WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} />;
      const msg = days_remaining <= 1
        ? <>{icon} Membership Premium kamu HABIS HARI INI! Perpanjang sekarang.</>
        : <>{icon} Membership Premium kamu akan habis dalam {days_remaining} hari ({dayjs(subscription_expires_at).format('DD MMM YYYY')}). Segera perpanjang!</>;
      
      return (
        <Alert
          message={msg}
          type={urgency}
          showIcon
          style={{ marginBottom: '20px', border: `2px solid ${urgency === 'error' ? '#e03131' : '#f59f00'}`, borderRadius: '8px' }}
          action={
            <Button
              size="small"
              style={{ background: '#1a202c', color: '#fff', border: 'none', fontWeight: 800 }}
              onClick={handleCheckout}
              loading={checkoutLoading}
            >
              Perpanjang
            </Button>
          }
        />
      );
    }
    
    if (subscription_status === 'expired') {
      return (
        <Alert
          message={<><CloseCircleTwoTone twoToneColor="#f5222d" style={{ marginRight: 8 }} /> Membership Premium kamu sudah berakhir. Perpanjang untuk kembali mengakses semua fitur.</>}
          type="error"
          showIcon
          style={{ marginBottom: '20px', border: '2px solid #e03131', borderRadius: '8px' }}
          action={
            <Button
              size="small"
              style={{ background: '#1a202c', color: '#fff', border: 'none', fontWeight: 800 }}
              onClick={handleCheckout}
              loading={checkoutLoading}
            >
              Berlangganan
            </Button>
          }
        />
      );
    }

    return null;
  };

  const renderPaymentStatus = (s: string) => {
    const colors: Record<string, string> = { paid: 'green', pending: 'gold', failed: 'red', expired: 'default' };
    const labels: Record<string, React.ReactNode> = { 
      paid: <><CheckCircleTwoTone twoToneColor="#52c41a" /> Lunas</>, 
      pending: <><ClockCircleTwoTone twoToneColor="#faad14" /> Menunggu</>, 
      failed: <><CloseCircleTwoTone twoToneColor="#f5222d" /> Gagal</>, 
      expired: <><ClockCircleTwoTone twoToneColor="#8c8c8c" /> Kedaluwarsa</> 
    };
    return <Tag color={colors[s] || 'default'}>{labels[s] || s}</Tag>;
  };

  // Helper component for QRIS steps
const StepItem: React.FC<{ number: number; text: string }> = ({ number, text }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
    <div style={{
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: '#1a202c',
      color: '#fab005',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 900,
      fontSize: '12px',
      flexShrink: 0,
      marginTop: '1px'
    }}>
      {number}
    </div>
    <span style={{ fontWeight: 600, color: '#343a40', lineHeight: '1.5' }}>{text}</span>
  </div>
);

if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
        <Spin size="large" />
        <span style={{ fontWeight: 800 }}>Memuat profil...</span>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '12px' }}>

      {/* Expiry Banner */}
      {renderSubscriptionBanner()}

      {/* Profile Form */}
      <Card style={{ border: '3px solid #1a202c', borderRadius: '16px', boxShadow: '4px 4px 0 #1a202c', marginBottom: '24px' }}>
        <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}><IdcardTwoTone twoToneColor="#1890ff" style={{ marginRight: 10, fontSize: '28px' }} /> Profil Lahir Kamu</Title>
          <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
            Kelola detail profil lahir kamu untuk kalkulasi bagan BaZi personal.
          </Paragraph>
        </div>

        {errorMsg && <Alert message={errorMsg} type="error" showIcon style={{ marginBottom: '20px', border: '2px solid #e03131', borderRadius: '8px' }} />}
        {successMsg && <Alert message={successMsg} type="success" showIcon style={{ marginBottom: '20px', border: '2px solid #2b8a3e', borderRadius: '8px' }} />}

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label={<span style={{ fontWeight: 800 }}>Nama Lengkap</span>} name="name" rules={[{ required: true, message: 'Masukkan nama lengkap' }]}>
            <Input placeholder="Masukkan nama kamu" style={{ height: '40px', border: '2px solid #1a202c', borderRadius: '8px' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Tanggal Lahir</span>} name="dob" rules={[{ required: true, message: 'Pilih tanggal lahir' }]}>
                <DatePicker inputReadOnly needConfirm style={{ width: '100%', height: '40px', border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir</span>} name="tob" rules={[{ required: !unknownTob, message: 'Pilih jam lahir' }]}>
                <TimePicker inputReadOnly format="HH:mm" disabled={unknownTob} style={{ width: '100%', height: '40px', border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Zona Waktu</span>} name="timezone" rules={[{ required: true }]}>
                <Select style={{ height: '40px' }}>
                  <Select.Option value="Asia/Jakarta">WIB - Asia/Jakarta (GMT+7)</Select.Option>
                  <Select.Option value="Asia/Makassar">WITA - Asia/Makassar (GMT+8)</Select.Option>
                  <Select.Option value="Asia/Jayapura">WIT - Asia/Jayapura (GMT+9)</Select.Option>
                  <Select.Option value="Asia/Singapore">Singapore (GMT+8)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Jenis Kelamin</span>} name="gender" rules={[{ required: true }]}>
                <Select style={{ height: '40px' }}>
                  <Select.Option value="male">Laki-laki ♂</Select.Option>
                  <Select.Option value="female">Perempuan ♀</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir Tidak Diketahui?</span>}>
            <Switch checked={unknownTob} onChange={setUnknownTob} style={{ background: unknownTob ? '#2b8a3e' : '#ced4da' }} />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={submitting} block
            style={{ height: '46px', background: '#fab005', borderColor: '#1a202c', color: '#1a202c', fontWeight: 900, fontSize: '1rem', boxShadow: '3px 3px 0 #1a202c', borderRadius: '10px', marginTop: '12px' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExperimentTwoTone twoToneColor="#1890ff" style={{ marginRight: 8, fontSize: '20px' }} /> Simpan & Hitung Ulang
            </span>
          </Button>
        </Form>

        <Divider style={{ borderTop: '2px solid #1a202c', margin: '24px 0' }} />

        {/* Account Details */}
        <div style={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px', border: '2px solid #1a202c' }}>
          <Title level={5} style={{ margin: '0 0 12px 0', fontWeight: 800 }}><IdcardTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Detail Akun</Title>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <Text style={{ fontWeight: 700 }}>Email:</Text> <Text>{userEmail}</Text>
            </div>
            <div>
              <Text style={{ fontWeight: 700 }}>Status Keanggotaan:</Text>{' '}
              {isAdmin ? (
                <Tag color="red" style={{ fontWeight: 800, border: '2px solid #c92a2a', display: 'inline-flex', alignItems: 'center', padding: '2px 8px' }}><SafetyCertificateTwoTone twoToneColor="#f5222d" style={{ marginRight: 6 }} /> Admin</Tag>
              ) : subscription?.subscription_status === 'premium' ? (
                <Tag color="gold" style={{ fontWeight: 800, border: '2px solid #d48806', display: 'inline-flex', alignItems: 'center', padding: '2px 8px' }}><StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 6 }} /> Premium</Tag>
              ) : (
                <Tag color="default" style={{ fontWeight: 800, display: 'inline-flex', alignItems: 'center', padding: '2px 8px' }}><IdcardTwoTone style={{ marginRight: 6 }} /> Free</Tag>
              )}
            </div>
            {subscription?.subscription_expires_at && !isAdmin && (
              <div>
                <Text style={{ fontWeight: 700 }}>Masa Aktif s/d:</Text>{' '}
                <Text style={{ fontWeight: 800, color: (subscription.days_remaining || 0) <= 3 ? '#e03131' : '#2b8a3e' }}>
                  {dayjs(subscription.subscription_expires_at).format('DD MMM YYYY')}
                  {subscription.days_remaining !== null && ` (${subscription.days_remaining} hari lagi)`}
                </Text>
              </div>
            )}
            {checksRemaining !== null && checksRemaining !== 9999 && (
              <div>
                <Text style={{ fontWeight: 700 }}>Kuota Harian Cek Orang Lain:</Text>{' '}
                <Text style={{ fontWeight: 800, color: checksRemaining === 0 ? '#e03131' : '#2b8a3e' }}>
                  {checksRemaining} kali tersisa
                </Text>
              </div>
            )}
            {isAdmin && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #dee2e6' }}>
                <Text style={{ fontWeight: 700, color: '#868e96', fontSize: '12px', display: 'inline-flex', alignItems: 'center' }}><ToolTwoTone twoToneColor="#868e96" style={{ marginRight: 6, fontSize: '14px' }} /> Admin Tools:</Text>
                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Button
                    size="small"
                    onClick={() => setAdminPreview(v => !v)}
                    style={{
                      background: adminPreview ? '#e03131' : '#1a202c',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 800,
                      fontSize: '11px',
                      borderRadius: '6px',
                      textAlign: 'left'
                    }}
                  >
                    {adminPreview ? <span style={{ display: 'inline-flex', alignItems: 'center' }}><EyeInvisibleTwoTone style={{ marginRight: 6, fontSize: '14px' }} /> Sembunyikan Preview Membership</span> : <span style={{ display: 'inline-flex', alignItems: 'center' }}><EyeTwoTone style={{ marginRight: 6, fontSize: '14px' }} /> Preview Tampilan Membership User</span>}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => navigate('/personal/admin/payments')}
                    style={{
                      background: '#2b8a3e',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 800,
                      fontSize: '11px',
                      borderRadius: '6px',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}><SafetyCertificateTwoTone twoToneColor="#52c41a" style={{ marginRight: 6, fontSize: '14px' }} /> Verifikasi Pembayaran QRIS</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Change Password Card */}
      <Card style={{ border: '3px solid #1a202c', borderRadius: '16px', boxShadow: '4px 4px 0 #1a202c', marginBottom: '24px' }}>
        <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '20px' }}>
          <Title level={4} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}>
            <LockOutlined style={{ marginRight: 10, fontSize: '24px', color: '#e03131' }} /> Ubah Password
          </Title>
          <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
            Gunakan password yang kuat dan tidak digunakan di akun lain.
          </Paragraph>
        </div>

        {pwErrorMsg && <Alert message={pwErrorMsg} type="error" showIcon style={{ marginBottom: '16px', border: '2px solid #e03131', borderRadius: '8px' }} />}
        {pwSuccessMsg && <Alert message={pwSuccessMsg} type="success" showIcon style={{ marginBottom: '16px', border: '2px solid #2b8a3e', borderRadius: '8px' }} />}

        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            label={<span style={{ fontWeight: 800 }}>Password Saat Ini</span>}
            name="current_password"
            rules={[{ required: true, message: 'Masukkan password saat ini' }]}
          >
            <Input.Password
              placeholder="Password saat ini"
              style={{ height: '40px', border: '2px solid #1a202c', borderRadius: '8px' }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={{ fontWeight: 800 }}>Password Baru</span>}
                name="new_password"
                rules={[
                  { required: true, message: 'Masukkan password baru' },
                  { min: 8, message: 'Minimal 8 karakter' },
                  { pattern: /[A-Z]/, message: 'Harus mengandung huruf kapital' },
                  { pattern: /[a-z]/, message: 'Harus mengandung huruf kecil' },
                  { pattern: /[0-9]/, message: 'Harus mengandung angka' }
                ]}
              >
                <Input.Password
                  placeholder="Password baru"
                  style={{ height: '40px', border: '2px solid #1a202c', borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={{ fontWeight: 800 }}>Konfirmasi Password Baru</span>}
                name="confirm_password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Konfirmasi password baru' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Password tidak cocok'));
                    }
                  })
                ]}
              >
                <Input.Password
                  placeholder="Ulangi password baru"
                  style={{ height: '40px', border: '2px solid #1a202c', borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* OWASP requirements hint */}
          <div style={{
            background: '#fff9e6',
            border: '2px solid #fab005',
            borderRadius: '8px',
            padding: '10px 14px',
            marginBottom: '16px',
            fontSize: '12px',
            color: '#495057',
            lineHeight: '1.6'
          }}>
            <div style={{ fontWeight: 800, marginBottom: '4px', color: '#1a202c' }}>Syarat Password:</div>
            <div>• Minimal 8 karakter</div>
            <div>• Minimal 1 huruf kapital (A-Z)</div>
            <div>• Minimal 1 huruf kecil (a-z)</div>
            <div>• Minimal 1 angka (0-9)</div>
            <div>• Tidak boleh sama dengan password saat ini</div>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            loading={changingPassword}
            style={{
              height: '42px',
              background: '#e03131',
              borderColor: '#1a202c',
              color: '#fff',
              fontWeight: 900,
              fontSize: '0.95rem',
              boxShadow: '3px 3px 0 #1a202c',
              borderRadius: '10px'
            }}
            block
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LockOutlined style={{ marginRight: 8, fontSize: '18px' }} /> Ubah Password
            </span>
          </Button>
        </Form>
      </Card>

      {/* Subscription Card */}
      {(!isAdmin || adminPreview) && (
        <Card style={{ border: '3px solid #1a202c', borderRadius: '16px', boxShadow: '4px 4px 0 #1a202c', marginBottom: '24px' }}>
          <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '20px' }}>
            <Title level={4} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}><CreditCardTwoTone twoToneColor="#1890ff" style={{ marginRight: 10, fontSize: '24px' }} /> Membership & Pembayaran</Title>
          </div>

          {/* Plan Selection */}
          <div style={{
            border: '2px solid #fab005',
            borderRadius: '12px',
            padding: '16px',
            background: 'linear-gradient(135deg, #fff9e6 0%, #fffbe8 100%)',
            marginBottom: '16px',
            boxShadow: '2px 2px 0 #1a202c'
          }}>
            <div style={{ fontWeight: 900, fontSize: '16px', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
              <StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 8, fontSize: '18px' }} /> Pilih Paket Premium
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {Object.entries(PLANS).map(([key, plan]) => {
                const isSelected = selectedPlan === key;
                return (
                  <div
                    key={key}
                    onClick={() => setSelectedPlan(key)}
                    style={{
                      flex: 1,
                      minWidth: '110px',
                      padding: '12px 10px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      border: isSelected ? '3px solid #1a202c' : '2px solid #dee2e6',
                      background: isSelected ? '#fff' : '#f8f9fa',
                      boxShadow: isSelected ? '3px 3px 0 #1a202c' : 'none',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}
                  >
                    {plan.badge && (
                      <span style={{
                        position: 'absolute',
                        top: '-9px',
                        right: '-5px',
                        background: '#fa5252',
                        color: '#fff',
                        padding: '1px 7px',
                        borderRadius: '20px',
                        fontSize: '9px',
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                      }}>
                        {plan.badge}
                      </span>
                    )}
                    <div style={{ fontWeight: 900, fontSize: '18px', color: '#e03131' }}>
                      Rp {plan.price.toLocaleString('id-ID')}
                    </div>
                    {plan.originalPrice && (
                      <div style={{ fontSize: '11px', color: '#adb5bd', textDecoration: 'line-through', marginTop: '1px' }}>
                        Rp {plan.originalPrice.toLocaleString('id-ID')}
                      </div>
                    )}
                    {plan.promoLabel && (
                      <div style={{ fontSize: '10px', fontWeight: 800, color: '#e03131', marginTop: '1px' }}>
                        🔥 {plan.promoLabel}
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#868e96', marginTop: '1px' }}>
                      / {plan.desc}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#2b8a3e', marginTop: '3px' }}>
                      {plan.perHari}
                    </div>
                    <div style={{ marginTop: '6px' }}>
                      <div style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        border: isSelected ? '4px solid #1a202c' : '2px solid #dee2e6',
                        margin: '0 auto',
                        transition: 'all 0.2s',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ fontSize: '13px', color: '#495057', fontWeight: 600 }}>
                Akses penuh semua fitur BaZi Insights — <strong>mulai dari Rp 1.000/hari!</strong>
              </div>
              <Button
                onClick={handleCheckout}
                loading={checkoutLoading}
                style={{
                  background: '#1a202c',
                  color: '#fab005',
                  border: '2px solid #1a202c',
                  fontWeight: 900,
                  height: '42px',
                  padding: '0 20px',
                  borderRadius: '10px',
                  boxShadow: '3px 3px 0 #fab005',
                  fontSize: '14px'
                }}
              >
                {subscription?.subscription_status === 'premium' ? <span style={{ display: 'inline-flex', alignItems: 'center' }}><SyncOutlined style={{ marginRight: 8, fontSize: '16px' }} /> Perpanjang</span> : <span style={{ display: 'inline-flex', alignItems: 'center' }}><RocketTwoTone twoToneColor="#faad14" style={{ marginRight: 8, fontSize: '16px' }} /> Langganan</span>}
              </Button>
            </div>
          </div>

          {/* Payment History */}
          <Title level={5} style={{ fontWeight: 800, margin: '16px 0 8px 0', display: 'flex', alignItems: 'center' }}><HistoryOutlined style={{ marginRight: 10, fontSize: '20px' }} /> Riwayat Pembayaran</Title>
          <div style={{ marginTop: '16px' }}>
            <List
              itemLayout="vertical"
              dataSource={paymentHistory}
              loading={loadingHistory}
              pagination={{ pageSize: 3, size: 'small', align: 'center' }}
              locale={{ emptyText: 'Belum ada riwayat pembayaran.' }}
              renderItem={(item: any) => (
                <List.Item
                  style={{
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '16px', color: '#1a202c' }}>
                        Rp {Number(item.amount).toLocaleString('id-ID')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#868e96', marginTop: '4px' }}>
                        {dayjs(item.created_at).format('DD MMM YYYY, HH:mm')}
                      </div>
                    </div>
                    <div>{renderPaymentStatus(item.status)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #dee2e6', fontSize: '12px' }}>
                    <div>
                      <Text style={{ fontWeight: 700, color: '#495057' }}>Invoice:</Text> <Text style={{ fontFamily: 'monospace' }}>{item.id.substring(0, 16)}...</Text>
                    </div>
                    {item.new_expires_at && (
                      <div>
                        <Text style={{ fontWeight: 700, color: '#495057' }}>Aktif s/d:</Text> {dayjs(item.new_expires_at).format('DD MMM YYYY')}
                      </div>
                    )}
                  </div>
                </List.Item>
              )}
            />
          </div>
        </Card>
      )}

      {/* Payment Modal with QRIS Instructions */}
      <Modal
        title={<><CreditCardTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Pembayaran Premium</>}
        open={showPaymentModal}
        onCancel={() => { setShowPaymentModal(false); fetchPaymentHistory(); fetchSubscription(); }}
        footer={[
          <Button
            key="done"
            type="primary"
            loading={confirmLoading}
            onClick={async () => {
              if (!checkoutInvoice) return;
              setConfirmLoading(true);
              try {
                const res = await fetch(`${API_URL}/api/v1/personal/subscription/checkout/${checkoutInvoice}/confirm`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({})
                });
                if (!res.ok) {
                  const err = await res.json();
                  throw new Error(err.error || 'Gagal');
                }
                message.success('Pembayaran telah dilaporkan! Admin akan memverifikasi dalam 1x24 jam.');
              } catch (err: any) {
                message.error(err.message || 'Gagal melaporkan pembayaran. Silakan hubungi admin.');
              } finally {
                setConfirmLoading(false);
                setShowPaymentModal(false);
                fetchPaymentHistory();
                fetchSubscription();
              }
            }}
            style={{
              background: '#1a202c',
              border: '2px solid #1a202c',
              color: '#fab005',
              fontWeight: 900,
              borderRadius: '8px',
              boxShadow: '2px 2px 0 #fab005'
            }}
          >
            Saya Sudah Bayar
          </Button>,
          <Button key="close" onClick={() => { setShowPaymentModal(false); fetchPaymentHistory(); fetchSubscription(); }}>
            Tutup
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '8px 0' }}>
          {/* Invoice info */}
          {checkoutInvoice && (
            <div style={{
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '8px 12px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12px'
            }}>
              <span style={{ fontWeight: 700 }}>Invoice: <span style={{ fontFamily: 'monospace' }}>{checkoutInvoice}</span></span>
              <Tag color="gold" style={{ fontWeight: 700 }}>Menunggu Pembayaran</Tag>
            </div>
          )}

          {/* Total Amount */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#868e96', marginBottom: '4px' }}>Total Pembayaran</div>
            <div style={{ fontSize: '32px', fontWeight: 900, color: '#1a202c' }}>Rp {checkoutAmount.toLocaleString('id-ID')}</div>
            {checkoutUniqueCode && checkoutBaseAmount && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                marginTop: '8px', padding: '6px 14px',
                background: '#fff3cd', border: '2px solid #f59f00',
                borderRadius: '8px', fontSize: '12px', fontWeight: 800
              }}>
                <span style={{ color: '#868e96' }}>Rp {checkoutBaseAmount.toLocaleString('id-ID')}</span>
                <span style={{ color: '#1a202c' }}>+</span>
                <span style={{ color: '#e03131' }}>kode unik <strong style={{ fontFamily: 'monospace', fontSize: '14px' }}>{String(checkoutUniqueCode).padStart(3, '0')}</strong></span>
                <span style={{ color: '#2b8a3e', fontSize: '10px', fontWeight: 700, background: '#ebfbee', padding: '2px 6px', borderRadius: '4px' }}>ANTI-DUPLIKAT</span>
              </div>
            )}
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#2b8a3e', marginTop: '8px' }}>
              Akses Premium {checkoutDays} hari
            </div>
          </div>

          {/* QRIS Section */}
          <div style={{
            border: '2px solid #1a202c',
            borderRadius: '12px',
            padding: '20px',
            background: '#fff',
            marginBottom: '16px'
          }}>
            <div style={{ fontWeight: 900, fontSize: '16px', textAlign: 'center', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ScanOutlined style={{ marginRight: 8, fontSize: '20px' }} /> Bayar via QRIS
            </div>

            {/* QRIS Image — Dynamic from 3QRIS engine or static fallback */}
            <div style={{
              width: '180px',
              height: '180px',
              margin: '0 auto 12px',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '2px solid #dee2e6'
            }}>
              <img
                src={qrisDataUrl || '/qris-bazi.png'}
                alt="QRIS Pembayaran BaZi Insights"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>

            {/* Download QRIS Button */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Button
                type="default"
                size="small"
                onClick={async () => {
                  try {
                    // If dynamic QRIS, download from data URL directly
                    if (qrisDataUrl) {
                      const a = document.createElement('a');
                      a.href = qrisDataUrl;
                      a.download = `qris-bazi-${checkoutInvoice || 'payment'}.png`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    } else {
                      const resp = await fetch('/qris-bazi.png');
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'qris-bazi.png';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }
                  } catch (e) {
                    message.error('Gagal download QRIS. Silakan screenshot manual.');
                  }
                }}
                style={{
                  border: '2px solid #1a202c',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '13px',
                  boxShadow: '1px 1px 0 #1a202c'
                }}
                icon={<DownloadOutlined style={{ fontSize: 14 }} />}
              >
                Download QRIS
              </Button>
            </div>

            {/* Steps */}
            <div style={{ fontSize: '13px' }}>
              <div style={{ fontWeight: 800, marginBottom: '12px', color: '#1a202c' }}>Langkah Pembayaran:</div>
              {qrisDataUrl ? (
                <>
                  <StepItem number={1} text={`Buka aplikasi dompet digital (GoPay, OVO, DANA, ShopeePay, M-Banking). Total: Rp ${checkoutAmount.toLocaleString('id-ID')}`} />
                  <StepItem number={2} text={`Pilih fitur Scan QR, lalu scan QRIS di atas langsung — nominal Rp ${checkoutAmount.toLocaleString('id-ID')} sudah terisi otomatis (${checkoutDays} hari)`} />
                  <StepItem number={3} text={`Konfirmasi pembayaran, lalu klik "Saya Sudah Bayar" di bawah`} />
                </>
              ) : (
                <>
                  <StepItem number={1} text="Simpan gambar QRIS di atas ke HP Anda" />
                  <StepItem number={2} text="Buka aplikasi dompet digital (GoPay, OVO, DANA, ShopeePay, M-Banking)" />
                  <StepItem number={3} text="Pilih fitur Scan QR atau Upload QR dari galeri" />
                  <StepItem number={4} text="Upload gambar QRIS ini, lalu ikuti instruksi untuk menyelesaikan pembayaran" />
                </>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <Alert
            message={<span style={{ fontWeight: 800 }}><SyncOutlined style={{ marginRight: 6 }} /> Butuh Bantuan?</span>}
            description={
              <span style={{ fontWeight: 600, fontSize: '13px' }}>
                Jika sudah membayar tapi membership belum aktif dalam 1x24 jam, hubungi{' '}
                <strong>admin@bazi.com</strong> dengan menyertakan bukti transfer dan invoice ID.
              </span>
            }
            type="info"
            showIcon
            style={{ border: '2px solid #1a202c', borderRadius: '8px', background: '#e7f5ff', fontSize: '13px' }}
          />
        </div>
      </Modal>
    </div>
  );
};
export default ProfilePage;
